import {
	BrokerAnalysePartnership,
	BrokerCommunication,
	BrokerDocument,
	BrokerTask,
	BrokersSocietiesWithUsers,
	GetBrokersSocietiesQueryParams,
	PaginatedResponse,
	SearchSocietiesPayload,
	SocietyUserVar,
} from "@dune/types";
import {
	keepPreviousData,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";

export const getAllBrokersSocieties = async (
	params: GetBrokersSocietiesQueryParams,
): Promise<PaginatedResponse<BrokersSocietiesWithUsers>> => {
	const { data } = await privateAxios.get<
		PaginatedResponse<BrokersSocietiesWithUsers>
	>("/societies/brokers-societies", {
		params: {
			search: params.search,
			status: params.status,
			page: params.page,
			limit: params.limit,
		},
	});
	return data;
};

export const useGetAllBrokersSocieties = (
	params: GetBrokersSocietiesQueryParams = {},
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: societiesQueryKeys.societiesBrokers(
			params.search,
			params.status,
			params.page,
			params.limit,
		),
		queryFn: () => getAllBrokersSocieties(params),
		placeholderData: keepPreviousData,
		enabled: options?.enabled ?? true,
	});
};

export const getBrokerDetails = async (id: string) => {
	const { data } = await privateAxios.get<BrokerAnalysePartnership>(
		`/societies/get-broker-details?id=${id}`,
	);
	return data;
};

export const useGetBrokerDetails = (id?: string) => {
	return useQuery({
		enabled: !!id,
		queryKey: societiesQueryKeys.brokerDetails(id),
		queryFn: () =>
			id ? getBrokerDetails(id) : Promise.reject("ID is undefined"),
		refetchOnMount: true,
		staleTime: 0,
	});
};

export const useGetBrokerDetailsSuspense = (id: string) => {
	return useSuspenseQuery({
		queryKey: societiesQueryKeys.brokerDetails(id),
		queryFn: () => getBrokerDetails(id),
		refetchOnMount: true,
		staleTime: 0,
	});
};

export const getSyndicSocieties = async (search: string) => {
	const { data } = await privateAxios.get<SearchSocietiesPayload[]>(
		"/societies/syndics",
		{
			params: {
				search,
			},
		},
	);
	return data;
};

export const useGetSyndicSocieties = (search: string) => {
	return useQuery({
		queryKey: societiesQueryKeys.listSyndics(search),
		queryFn: () => getSyndicSocieties(search),
	});
};

export const getBrokerCommunications = async (
	brokerId: number,
	params?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	const { data } = await privateAxios.get<{
		data: BrokerCommunication[];
		meta: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		};
	}>(`/societies/broker/${brokerId}/communications`, {
		params,
	});
	return data;
};

export const getBrokerUnprocessedCommunicationsCount = async (
	brokerId: number,
) => {
	const { data } = await privateAxios.get<{ count: number }>(
		`/societies/broker/${brokerId}/communications/unprocessed-count`,
	);
	return data.count;
};

export const useGetBrokerCommunications = (
	brokerId: number,
	params?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	return useQuery({
		queryKey: societiesQueryKeys.brokerCommunications(brokerId, params),
		queryFn: () => getBrokerCommunications(brokerId, params),
		enabled: !!brokerId,
		placeholderData: keepPreviousData,
	});
};

export const useGetBrokerUnprocessedCommunicationsCount = (
	brokerId: number,
) => {
	return useQuery({
		queryKey: ["broker-unprocessed-communications-count", brokerId],
		queryFn: () => getBrokerUnprocessedCommunicationsCount(brokerId),
		enabled: !!brokerId,
		refetchInterval: 30000, // Refresh every 30 seconds
	});
};

export const getBrokerDocuments = async (brokerId: number) => {
	const { data } = await privateAxios.get<BrokerDocument[]>(
		`/societies/broker/${brokerId}/documents`,
	);
	return data;
};

export const getBrokerUsers = async (brokerId: number) => {
	const { data } = await privateAxios.get<SocietyUserVar[]>(
		`/societies/broker/${brokerId}/users`,
	);
	return data;
};

export const useGetBrokerDocuments = (brokerId: number) => {
	return useQuery({
		enabled: !!brokerId,
		queryKey: societiesQueryKeys.brokerDocuments(brokerId),
		queryFn: () => getBrokerDocuments(brokerId),
	});
};

export const useGetBrokerUsers = (brokerId: number) => {
	return useQuery({
		queryKey: societiesQueryKeys.brokerUsers(brokerId),
		queryFn: () => getBrokerUsers(brokerId),
		enabled: !!brokerId,
	});
};

export const getBrokerEntities = async (brokerId: number) => {
	const { data } = await privateAxios.get<string[]>(
		`/societies/broker/${brokerId}/entities`,
	);
	return data;
};

export const getBrokerTasks = async (brokerId: number) => {
	const { data } = await privateAxios.get<BrokerTask[]>(
		`/societies/broker/${brokerId}/tasks`,
	);
	return data;
};

export const useGetBrokerEntities = (brokerId: number) => {
	return useQuery({
		queryKey: societiesQueryKeys.brokerEntities(brokerId),
		queryFn: () => getBrokerEntities(brokerId),
		enabled: !!brokerId,
	});
};

export const useGetBrokerTasks = (brokerId: number) => {
	return useQuery({
		queryKey: societiesQueryKeys.brokerTasks(brokerId),
		queryFn: () => getBrokerTasks(brokerId),
		enabled: !!brokerId,
	});
};

export const getInsurerSocieties = async () => {
	const { data } = await privateAxios.get<SearchSocietiesPayload[]>(
		"/societies/insurers",
	);
	return data;
};

export const useGetInsurerSocieties = () => {
	return useQuery({
		queryKey: societiesQueryKeys.listInsurers(""),
		queryFn: () => getInsurerSocieties(),
	});
};

export const getTPASocieties = async (search: string) => {
	const { data } = await privateAxios.get<SearchSocietiesPayload[]>(
		"/societies/tpas",
		{
			params: {
				search,
			},
		},
	);
	return data;
};

export const useGetTPASocieties = (search: string) => {
	return useQuery({
		queryKey: societiesQueryKeys.listTPAs(search),
		queryFn: () => getTPASocieties(search),
	});
};

export const useGetSocieties = (params?: {
	search?: string;
	limit?: number;
}) => {
	return useQuery({
		queryKey: [...societiesQueryKeys.all, params],
		queryFn: async () => {
			const queryParams = new URLSearchParams();
			if (params?.search) queryParams.append("search", params.search);
			if (params?.limit) queryParams.append("limit", params.limit.toString());

			const response = await privateAxios.get<
				{
					id: number;
					siren: string;
					denomination: string;
				}[]
			>(`/societies?${encodeURIComponent(queryParams.toString())}`);
			return response.data;
		},
		enabled: true,
	});
};

export const getSocietyFiles = async (societyId: number, type?: string) => {
	const params = type ? { type } : {};
	const { data } = await privateAxios.get<
		{
			id: number;
			createdAt: string;
			file: {
				id: string;
				name: string;
				mimeType: string;
				key: string;
				bucket: string;
				size: number | null;
				createdAt: string;
				updatedAt: string;
				fileData?: {
					metadata?: {
						envelopeId?: string;
						[key: string]: unknown;
					};
				};
				docuSignEnvelopes?: {
					id: string;
					envelopeId: string;
					status: string;
				}[];
			};
		}[]
	>(`/societies/${societyId}/files`, { params });
	return data;
};

export const useGetSocietyFiles = (societyId: number, type?: string) => {
	return useQuery({
		queryKey: societiesQueryKeys.societyFiles(societyId, type),
		queryFn: () => getSocietyFiles(societyId, type),
		enabled: !!societyId,
	});
};
